import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { ArrowRight, X } from "@phosphor-icons/react";
import { mask } from '../lib/privacy';

/**
 * VictoryLapScreen, full-screen post-Income-Map takeover.
 * Replaces the generic "your report is on its way" toast + immediate upsell modal
 * with a Level 2-style choreographed reveal. Copywriter can swap all headlines
 * and CTAs via the props below.
 *
 * Props:
 *   weeklyTarget:  number, customer's calculated weekly number (drives the big display)
 *   goalStatement: string, their goal, e.g. "hit your Mexico goal"
 *   copy: {
 *     preHeader:   "LEVEL 2 UNLOCKED"        (copywriter tweaks)
 *     headline:    "You just found your number." (copywriter rewrites)
 *     unitLabel:   "/week"                    (design constant, unlikely to change)
 *     detail:      dynamic string using goalStatement + weeklyTarget
 *     ctaPrimary:  "Ready for Level 2? →"    (copywriter rewrites)
 *     ctaSecondary:"See my full breakdown first"
 *   }
 *   onCTAClick:    () => void, fired when user clicks primary CTA (opens upsell)
 *   onDismiss:     () => void, fired when user clicks secondary link
 */
const COLOR = {
  gold: "#F5CB5C",
  goldDeep: "#C99B33",
  red: "#DE3C4B",
  ink: "#2A1E5C",
  inkLight: "#3D2E7A",
  turquoise: "#2E86AB",
  cream: "#FAF7F0",
};

const fmtMoney = (n) =>
  n == null ? ", " : mask(`$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

export default function VictoryLapScreen({
  weeklyTarget,
  goalStatement,
  onCTAClick,
  onDismiss,
  copy = {},
}) {
  const {
    preHeader = "LEVEL 2 UNLOCKED",
    headline = "You just found your number.",
    unitLabel = "/week",
    detailPrefix = "That's what it takes to",
    ctaPrimary = "Ready for Level 2? →",
    ctaSecondary = "See my full breakdown first",
  } = copy;

  const [phase, setPhase] = useState(0); // 0=preheader, 1=headline+number, 2=detail, 3=cta
  const firedRef = useRef(false);

  useEffect(() => {
    // Staggered choreography, reveal each phase in sequence
    const t1 = setTimeout(() => setPhase(1), 400);
    const t2 = setTimeout(() => setPhase(2), 1400);
    const t3 = setTimeout(() => setPhase(3), 2200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  useEffect(() => {
    if (phase < 1 || firedRef.current) return;
    firedRef.current = true;
    // Small confetti burst when the number reveals
    const colors = [COLOR.gold, COLOR.red, COLOR.ink, COLOR.turquoise];
    confetti({
      particleCount: 90,
      spread: 70,
      origin: { y: 0.4 },
      colors,
      scalar: 0.9,
    });
  }, [phase]);

  // Trim goal statement into a shorter sentence fragment when possible
  const cleanGoal = (() => {
    if (!goalStatement) return "hit your goal";
    // Find "so I can X", trim at " and " / ", " / period / end (skip commas inside numbers)
    const m = goalStatement.match(/so (?:I|you) can (.+?)(?:\s+and\s+|\s+, \s+|\.|$)/i);
    if (m) {
      const s = m[1].trim().replace(/[,\.]$/, '');
      return s.length > 80 ? s.slice(0, 77) + "…" : s;
    }
    return goalStatement.length > 90
      ? goalStatement.slice(0, 87).trim() + "…"
      : goalStatement;
  })();

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 overflow-y-auto py-8"
      style={{
        background: `radial-gradient(circle at 50% 30%, ${COLOR.inkLight} 0%, ${COLOR.ink} 100%)`,
      }}
      data-testid="victory-lap-screen"
    >
      {/* Dismiss X - big, pinned to viewport so it never scrolls away */}
      <button
        onClick={onDismiss}
        aria-label="Close"
        className="fixed top-4 right-4 z-20 w-11 h-11 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110"
        style={{ background: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.6)" }}
        data-testid="victory-close-btn"
      >
        <X className="w-6 h-6" style={{ color: "#fff" }} />
      </button>

      <div className="max-w-xl w-full text-center">
        {/* Pre-header pill */}
        <div
          className={`inline-block px-4 py-1.5 rounded-full mb-6 transition-all duration-500 ${
            phase >= 0 ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
          }`}
          style={{
            background: `${COLOR.gold}22`,
            border: `1px solid ${COLOR.gold}55`,
          }}
          data-testid="victory-preheader"
        >
          <span
            className="text-xs font-bold uppercase tracking-widest"
            style={{ color: COLOR.gold }}
          >
            🏁 {preHeader}
          </span>
        </div>

        {/* Headline */}
        <h1
          className={`font-heading text-3xl sm:text-5xl font-medium leading-tight mb-8 transition-all duration-700 ${
            phase >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
          style={{ color: "#fff", fontFamily: "'Fraunces', Georgia, serif" }}
          data-testid="victory-headline"
        >
          {headline}
        </h1>

        {/* Big number */}
        <div
          className={`mb-8 transition-all duration-700 delay-100 ${
            phase >= 1 ? "opacity-100 scale-100" : "opacity-0 scale-90"
          }`}
        >
          <div
            className="font-bold leading-none"
            style={{
              color: COLOR.gold,
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: "clamp(4rem, 12vw, 7rem)",
              textShadow: `0 4px 40px ${COLOR.gold}44`,
            }}
            data-testid="victory-number"
          >
            {fmtMoney(weeklyTarget)}
          </div>
          <div
            className="mt-2 text-sm font-semibold uppercase tracking-widest"
            style={{ color: "rgba(255,255,255,0.6)" }}
          >
            {unitLabel}
          </div>
        </div>

        {/* Detail, copywriter-friendly personalization slot */}
        <p
          className={`text-base sm:text-lg leading-relaxed mb-10 max-w-md mx-auto transition-all duration-700 ${
            phase >= 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
          style={{ color: "rgba(255,255,255,0.85)" }}
          data-testid="victory-detail"
        >
          {detailPrefix} <span style={{ color: COLOR.gold, fontWeight: 600 }}>{cleanGoal}</span>.
          <br className="hidden sm:inline" />
          <span style={{ color: "rgba(255,255,255,0.6)" }} className="text-sm sm:text-base">
            Ready to turn that number into a week-by-week rhythm?
          </span>
        </p>

        {/* CTAs */}
        <div
          className={`transition-all duration-700 delay-200 ${
            phase >= 3 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <button
            onClick={onCTAClick}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-transform hover:scale-105"
            style={{
              background: COLOR.gold,
              color: COLOR.ink,
              boxShadow: `0 8px 32px ${COLOR.gold}55`,
            }}
            data-testid="victory-cta-primary"
          >
            {ctaPrimary}
          </button>
          <div className="mt-4">
            <button
              onClick={onDismiss}
              className="text-sm underline transition-opacity hover:opacity-70"
              style={{ color: "rgba(255,255,255,0.6)" }}
              data-testid="victory-cta-secondary"
            >
              {ctaSecondary}
            </button>
          </div>
        </div>

        {/* Placeholder marker, remove after copywriter delivers */}
        <p
          className="mt-8 text-[10px] italic uppercase tracking-wider"
          style={{ color: "rgba(255,255,255,0.3)" }}
        >
          Copywriter draft slot, swap headline &amp; CTAs via props
        </p>
      </div>
    </div>
  );
}
